import React from "react";
import autoBind from "react-autobind";
import { Button, ButtonToolbar, ButtonGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import request from "superagent";
import { getImagesEdit } from "../../ai/aiAssistant";
import env from "../../constants/env";
import { refreshCredentials } from "../../utils/securityUtil";
import { getJwtToken } from "../../actions/user";
import ColorPicker from "./colorPicker";

const MIN_FREE_CROP_SCALE = 0.35;
const MAX_CROP_ZOOM = 4;
const DEFAULT_CROP_ZOOM = 1;
const FREE_CROP_PRESET = "free";
const MAX_BLUR_PX = 24;
const MAX_PADDING_SLIDER_PX = 240;
const MAX_PADDING_INPUT_PX = 4096;
const MAX_FINAL_OUTPUT_DIMENSION_PX = 8192;
const MARKUP_ZOOM_OPTIONS = [25, 33, 50, 66, 75, 90, 100, 110, 125, 150, 200];
const MARKUP_BORDER_WIDTH_OPTIONS = Array.from({ length: 17 }, (_, index) => index);
const MARKUP_BORDER_RADIUS_OPTIONS = Array.from({ length: 65 }, (_, index) => index);
const AI_IMAGE_SIZES = [256, 512, 1024];
const CROP_PRESET_OPTIONS = [
	{ value: "16:9", label: "16:9", aspectRatio: 16 / 9 },
	{ value: "4:3", label: "4:3", aspectRatio: 4 / 3 },
	{ value: "1:1", label: "1:1", aspectRatio: 1 },
	{ value: "3:4", label: "3:4", aspectRatio: 3 / 4 },
	{ value: "9:16", label: "9:16", aspectRatio: 9 / 16 },
	{ value: FREE_CROP_PRESET, label: "Free", aspectRatio: null }
];

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max);
}

function normalizePngFileName(fileName = "") {
	const trimmed = `${fileName || ""}`.trim();
	if (!trimmed) {
		return "image-edited.png";
	}
	return trimmed.toLowerCase().endsWith(".png") ? trimmed : `${trimmed}.png`;
}

function getFileNameStemFromValue(value = "") {
	const rawValue = `${value || ""}`.trim();
	if (!rawValue) {
		return "";
	}

	const withoutQuery = rawValue.split("?")[0].split("#")[0];
	const lastSegment = withoutQuery.split("/").pop() || withoutQuery;
	let decodedSegment = lastSegment;
	try {
		decodedSegment = decodeURIComponent(lastSegment);
	} catch (err) {
		decodedSegment = lastSegment;
	}

	return decodedSegment.replace(/\.[^/.]+$/, "");
}

function isCanvasSafeUrl(url = "") {
	const value = `${url || ""}`.trim();
	if (!value) {
		return false;
	}
	if (/^(blob:|data:)/i.test(value)) {
		return true;
	}
	try {
		return new URL(value, window.location.href).origin === window.location.origin;
	} catch (err) {
		return false;
	}
}

function getBestAiSquareSize(maxDimension = 0) {
	const target = Math.max(Math.round(maxDimension || 0), 1);
	const found = AI_IMAGE_SIZES.find((size) => target <= size);
	return found || AI_IMAGE_SIZES[AI_IMAGE_SIZES.length - 1] || 1024;
}

function createMarkupGuid() {
	return `markup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getMarkupDefaults(componentType = "highlight") {
	if (componentType === "hotspot") {
		return {
			shape: "rectangle",
			borderColor: "rgba(255,140,0,1)",
			borderWidth: 5,
			borderRadius: 10,
			fillColor: "rgba(255,140,0,0.2)"
		};
	}

	return {
		shape: "rectangle",
		borderColor: "rgba(0,0,255,1)",
		borderWidth: 5,
		borderRadius: 0,
		fillColor: "rgba(0,0,255,0.2)"
	};
}

function colorResultToRgba(colorResult, fallback = "rgba(0,0,0,1)") {
	if (typeof colorResult === "string" && colorResult.trim()) {
		return colorResult;
	}

	const rgb = colorResult && colorResult.rgb ? colorResult.rgb : null;
	if (!rgb) {
		return fallback;
	}

	const alpha = rgb.a !== undefined ? rgb.a : 1;
	return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function isTransparentMarkupColor(colorValue = "") {
	const value = `${colorValue || ""}`.trim().toLowerCase();
	return !value || value === "transparent" || value === "rgba(0,0,0,0)" || value === "rgba(0, 0, 0, 0)";
}

function normalizeMarkupRotation(rotationValue = 0) {
	const numericRotation = Number(rotationValue);
	if (!Number.isFinite(numericRotation)) {
		return 0;
	}

	let normalizedRotation = numericRotation % 360;
	if (normalizedRotation > 180) {
		normalizedRotation -= 360;
	} else if (normalizedRotation < -180) {
		normalizedRotation += 360;
	}

	return normalizedRotation;
}

function rotatePointAroundCenter(pointX, pointY, centerX, centerY, rotationDegrees = 0) {
	const radians = (Number(rotationDegrees || 0) * Math.PI) / 180;
	const cos = Math.cos(radians);
	const sin = Math.sin(radians);
	const translatedX = pointX - centerX;
	const translatedY = pointY - centerY;

	return {
		x: centerX + (translatedX * cos) - (translatedY * sin),
		y: centerY + (translatedX * sin) + (translatedY * cos)
	};
}

export default class ImageEditorModal extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this.imageRef = React.createRef();
		this.canvasRef = React.createRef();
		this.cropViewportRef = React.createRef();
		this.markupSvgRef = React.createRef();
		this.dragState = null;
		this.markupPressAndHoldTimer = null;
		this.markupPressAndHoldCount = 0;
		this.markupPointerDrag = null;
		this.markupRotateModifierActive = false;
		this.openNonce = 0;
		this.previewObjectUrl = null;
		this.state = this.getInitialState(props);
	}

	componentDidMount() {
		window.addEventListener("resize", this.handleWindowResize);
		window.addEventListener("keydown", this.handleWindowKeyDown);
		window.addEventListener("keyup", this.handleWindowKeyUp);
		if (this.props.open) {
			this.resetForOpen(this.props);
		}
	}

	componentDidUpdate(prevProps) {
		if (this.props.open && !prevProps.open) {
			this.resetForOpen(this.props);
			return;
		}

		if (!this.props.open && prevProps.open) {
			this.openNonce += 1;
			this.revokePreviewObjectUrl();
		}

		if (
			this.props.open &&
			(prevProps.imageUrl !== this.props.imageUrl || prevProps.defaultImageName !== this.props.defaultImageName)
		) {
			this.resetForOpen(this.props);
			return;
		}

		if (this.props.open) {
			this.syncCropViewportSize();
		}
	}

	componentWillUnmount() {
		this.openNonce += 1;
		window.removeEventListener("resize", this.handleWindowResize);
		window.removeEventListener("keydown", this.handleWindowKeyDown);
		window.removeEventListener("keyup", this.handleWindowKeyUp);
		this.detachCropDragListeners();
		this.stopMarkupPressAndHold();
		this.detachMarkupPointerListeners();
		this.revokePreviewObjectUrl();
	}

	getInitialState(props = this.props) {
		const defaultImageName = this.getDefaultImageName(props);
		return {
			imageName: defaultImageName,
			customImageName: defaultImageName,
			autoLinkImage: props.defaultAutoLink !== false,
			overwriteImage: false,
			previewImageUrl: "",
			previewImageLoading: !!props.imageUrl,
			previewExportSafe: false,
			originalPreviewImageUrl: "",
			sourceReady: false,
			sourceImageWidth: 0,
			sourceImageHeight: 0,
			cropViewportWidth: 0,
			cropViewportHeight: 0,
			cropPreset: FREE_CROP_PRESET,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1,
			isDraggingCrop: false,
			saving: false,
			error: "",
			aiPrompt: "",
			aiEditing: false,
			aiError: "",
			hasAiEdits: false,
			blurPx: 0,
			paddingPx: 0,
			padToTargetSize: false,
			padTargetWidth: "",
			padTargetHeight: "",
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupComponentType: "highlight",
			markupShape: "rectangle",
			markupBorderColor: getMarkupDefaults("highlight").borderColor,
			markupBorderWidth: getMarkupDefaults("highlight").borderWidth,
			markupBorderRadius: getMarkupDefaults("highlight").borderRadius,
			markupFillEnabled: false,
			markupFillColor: getMarkupDefaults("highlight").fillColor,
			markupRotation: 0,
			markupPreviewScale: 100,
			markupShowItemIds: true,
			markupMouseX: 0,
			markupMouseY: 0,
			markupMouseDown: false,
			markupStartX: 0,
			markupStartY: 0,
			markupCurrentX: 0,
			markupCurrentY: 0,
			resizeEnabled: false,
			resizeWidth: "",
			resizeHeight: "",
			resizeLockAspect: true
		};
	}

	getDefaultImageName(props = this.props) {
		if (props.defaultImageName) {
			return normalizePngFileName(props.defaultImageName);
		}

		const fileStem =
			getFileNameStemFromValue(props.imageFileName) ||
			getFileNameStemFromValue(props.imageUrl) ||
			getFileNameStemFromValue(props.defaultBaseName) ||
			"image";

		const normalizedStem = /-edited$/i.test(fileStem) ? fileStem : `${fileStem}-edited`;
		return normalizePngFileName(normalizedStem);
	}

	getOverwriteImageName(props = this.props) {
		const overwritePathName = getFileNameStemFromValue(props.overwriteTargetPath);
		if (overwritePathName) {
			return normalizePngFileName(overwritePathName);
		}

		const originalImageName = `${props.originalImageName || ""}`.trim();
		if (originalImageName) {
			return originalImageName;
		}

		const fileName =
			getFileNameStemFromValue(props.imageFileName) ||
			getFileNameStemFromValue(props.imageUrl) ||
			getFileNameStemFromValue(props.defaultBaseName) ||
			"image";

		return normalizePngFileName(fileName);
	}

	resetForOpen(props = this.props) {
		this.openNonce += 1;
		this.revokePreviewObjectUrl();
		this.detachCropDragListeners();
		this.setState(this.getInitialState(props), () => {
			this.prepareImageSource(props);
		});
	}

	revokePreviewObjectUrl() {
		if (this.previewObjectUrl && typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
			URL.revokeObjectURL(this.previewObjectUrl);
		}
		this.previewObjectUrl = null;
	}

	base64ToBlob(base64Value = "", contentType = "application/octet-stream") {
		const binaryString = window.atob(base64Value);
		const binaryLength = binaryString.length;
		const bytes = new Uint8Array(binaryLength);
		for (let i = 0; i < binaryLength; i += 1) {
			bytes[i] = binaryString.charCodeAt(i);
		}
		return new Blob([bytes], { type: contentType || "application/octet-stream" });
	}

	async tryCreateDriveBlobPreviewUrl(objectGuid) {
		if (!objectGuid || !env?.apiGateway?.endpoints?.drive?.getObjectContent) {
			return "";
		}

		await refreshCredentials();
		const response = await request
			.get(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.drive.getObjectContent(objectGuid)}`)
			.set({ Authorization: getJwtToken(), "Content-Type": "application/json" });
		const payload = response && response.body ? response.body : {};
		const base64Value = payload?.base64 || payload?.body || "";
		if (!base64Value) {
			throw new Error("Image content response did not include base64 data.");
		}

		const blob = this.base64ToBlob(base64Value, payload?.contentType || payload?.content_type || "application/octet-stream");
		if (!blob.size) {
			throw new Error("Image content response was empty.");
		}

		if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
			return "";
		}

		return URL.createObjectURL(blob);
	}

	async tryCreateBlobPreviewUrl(imageUrl) {
		if (!imageUrl) {
			return "";
		}

		if (/^(blob:|data:)/i.test(imageUrl)) {
			return imageUrl;
		}

		const response = await fetch(imageUrl, {
			credentials: "include"
		});
		if (!response.ok) {
			throw new Error(`Unable to load image for editing (${response.status}).`);
		}

		const blob = await response.blob();
		if (!blob || !blob.size) {
			return "";
		}

		if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
			return "";
		}

		return URL.createObjectURL(blob);
	}

	async prepareImageSource(props = this.props) {
		const directUrl = props.imageUrl || "";
		const driveObjectGuid = props.driveObjectGuid || "";
		const nonce = this.openNonce;

		if (!directUrl && !driveObjectGuid) {
			this.setState({
				previewImageUrl: "",
				previewImageLoading: false,
				error: "No image URL was available for editing."
			});
			return;
		}

		this.setState({
			previewImageUrl: "",
			previewImageLoading: true,
			previewExportSafe: false,
			error: ""
		});

		try {
			const blobUrl = driveObjectGuid
				? await this.tryCreateDriveBlobPreviewUrl(driveObjectGuid)
				: await this.tryCreateBlobPreviewUrl(directUrl);
			if (nonce !== this.openNonce || !blobUrl) {
				return;
			}
			if (blobUrl !== directUrl) {
				this.previewObjectUrl = blobUrl;
			}
			this.setState({
				previewImageUrl: blobUrl,
				previewExportSafe: true
			});
		} catch (err) {
			console.warn("Unable to preload editable image as blob, falling back to direct URL preview.", err);
			if (nonce === this.openNonce) {
				this.setState({
					previewImageUrl: directUrl,
					previewImageLoading: false,
					previewExportSafe: isCanvasSafeUrl(directUrl),
					error: isCanvasSafeUrl(directUrl)
						? ""
					: "The image preview loaded, but the browser blocked export access to the source image. Save is disabled until a canvas-safe source is available."
				});
			}
		}
	}

	getImageElement() {
		return this.imageRef && this.imageRef.current ? this.imageRef.current : null;
	}

	getCanvasElement() {
		return this.canvasRef && this.canvasRef.current ? this.canvasRef.current : null;
	}

	getCropViewportElement() {
		return this.cropViewportRef && this.cropViewportRef.current ? this.cropViewportRef.current : null;
	}

	handleWindowResize() {
		window.requestAnimationFrame(() => {
			this.syncCropViewportSize();
		});
	}

	handleWindowKeyDown(event) {
		if (!this.props.open || !this.state.showMarkupModal || !this.state.selectedMarkupGuid) {
			return;
		}

		const targetTag = event && event.target && event.target.tagName ? event.target.tagName.toLowerCase() : "";
		const isEditableTarget =
			targetTag === "input" ||
			targetTag === "textarea" ||
			targetTag === "select" ||
			(event && event.target && event.target.isContentEditable);
		if (isEditableTarget) {
			return;
		}

		const key = `${event.key || ""}`.toLowerCase();
		if (key === "r") {
			this.markupRotateModifierActive = true;
			return;
		}

		if (!["arrowleft", "arrowright", "arrowup", "arrowdown"].includes(key)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		if (this.markupRotateModifierActive) {
			if (key === "arrowleft" || key === "arrowup") {
				this.handleRotateSelectedMarkup(-1);
			} else if (key === "arrowright" || key === "arrowdown") {
				this.handleRotateSelectedMarkup(1);
			}
			return;
		}

		if (key === "arrowleft") {
			this.handleNudgeSelectedMarkup(-1, 0);
		} else if (key === "arrowright") {
			this.handleNudgeSelectedMarkup(1, 0);
		} else if (key === "arrowup") {
			this.handleNudgeSelectedMarkup(0, -1);
		} else if (key === "arrowdown") {
			this.handleNudgeSelectedMarkup(0, 1);
		}
	}

	handleWindowKeyUp(event) {
		const key = `${event.key || ""}`.toLowerCase();
		if (key === "r") {
			this.markupRotateModifierActive = false;
		}
	}

	syncCropViewportSize() {
		const viewport = this.getCropViewportElement();
		if (!viewport) {
			return;
		}

		const computedStyle = window.getComputedStyle(viewport);
		const horizontalPadding =
			(parseFloat(computedStyle.paddingLeft) || 0) +
			(parseFloat(computedStyle.paddingRight) || 0);
		const verticalPadding =
			(parseFloat(computedStyle.paddingTop) || 0) +
			(parseFloat(computedStyle.paddingBottom) || 0);
		const nextWidth = Math.round(Math.max((viewport.clientWidth || 0) - horizontalPadding, 0));
		const nextHeight = Math.round(Math.max((viewport.clientHeight || 0) - verticalPadding, 0));
		if (!nextWidth || !nextHeight) {
			return;
		}

		if (
			nextWidth !== this.state.cropViewportWidth ||
			nextHeight !== this.state.cropViewportHeight
		) {
			this.setState({
				cropViewportWidth: nextWidth,
				cropViewportHeight: nextHeight
			});
		}
	}

	handleImageNameChange(event) {
		const imageName = event && event.target ? event.target.value : "";
		this.setState({
			imageName,
			customImageName: imageName
		});
	}

	handleToggleAutoLinkImage(event) {
		this.setState({
			autoLinkImage: !!(event && event.target ? event.target.checked : false)
		});
	}

	handleToggleOverwriteImage(event) {
		const overwriteImage = !!(event && event.target ? event.target.checked : false);
		this.setState((prevState) => ({
			overwriteImage,
			imageName: overwriteImage
				? this.getOverwriteImageName()
				: (prevState.customImageName || this.getDefaultImageName())
		}));
	}

	handleImageLoaded() {
		const image = this.getImageElement();
		if (!image) {
			return;
		}

		const sourceImageWidth = image.naturalWidth || image.width || 0;
		const sourceImageHeight = image.naturalHeight || image.height || 0;

		this.setState((prevState) => ({
			sourceReady: !!(sourceImageWidth && sourceImageHeight),
			sourceImageWidth,
			sourceImageHeight,
			previewImageLoading: false,
			error: "",
			previewExportSafe: prevState.previewExportSafe || isCanvasSafeUrl(prevState.previewImageUrl),
			originalPreviewImageUrl: prevState.originalPreviewImageUrl || prevState.previewImageUrl
		}), () => {
			window.requestAnimationFrame(() => {
				this.syncCropViewportSize();
			});
		});
	}

	handleAiPromptChange(event) {
		this.setState({
			aiPrompt: event && event.target ? event.target.value : ""
		});
	}

	applyPreviewImageUrl(nextPreviewImageUrl, extraState = {}) {
		this.setState({
			previewImageUrl: nextPreviewImageUrl || "",
			previewImageLoading: !!nextPreviewImageUrl,
			previewExportSafe: isCanvasSafeUrl(nextPreviewImageUrl),
			sourceReady: false,
			sourceImageWidth: 0,
			sourceImageHeight: 0,
			cropPreset: FREE_CROP_PRESET,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1,
			blurPx: 0,
			paddingPx: 0,
			padToTargetSize: false,
			padTargetWidth: "",
			padTargetHeight: "",
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupComponentType: "highlight",
			markupShape: "rectangle",
			markupBorderColor: getMarkupDefaults("highlight").borderColor,
			markupBorderWidth: getMarkupDefaults("highlight").borderWidth,
			markupBorderRadius: getMarkupDefaults("highlight").borderRadius,
			markupFillEnabled: false,
			markupFillColor: getMarkupDefaults("highlight").fillColor,
			markupRotation: 0,
			markupShowItemIds: true,
			markupMouseDown: false,
			resizeEnabled: false,
			resizeWidth: "",
			resizeHeight: "",
			resizeLockAspect: true,
			error: "",
			...extraState
		});
	}

	async handleApplyAiEdit() {
		const aiPrompt = `${this.state.aiPrompt || ""}`.trim();
		if (!aiPrompt) {
			this.setState({
				aiError: "This capability is currently unavailable."
			});
			return;
		}
		if (!this.state.sourceReady || this.state.previewImageLoading) {
			this.setState({
				aiError: "Wait for the image preview to finish loading first."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				aiError: "This image preview is not yet available for export-safe editing. Save is disabled until the canvas-safe source finishes loading."
			});
			return;
		}

		this.setState({
			aiEditing: true,
			aiError: ""
		});

		try {
			const aiPayload = this.createAiEditPayload();
			const blob = await this.canvasToBlob(aiPayload.imageCanvas);
			const maskBlob = aiPayload.maskCanvas ? await this.canvasToBlob(aiPayload.maskCanvas) : null;
			const constrainedPrompt = `Preserve the current composition, orientation, and framing as closely as possible. Do not zoom, crop, rotate, or recompose unless the request explicitly asks for it. Maintain the visible subject and current aspect presentation. Requested edit: ${aiPrompt}`;
			const result = await getImagesEdit({
				prompt: constrainedPrompt,
				imageBlob: blob,
				maskBlob,
				imageName: normalizePngFileName(this.state.imageName || "image-edited.png"),
				size: aiPayload.size
			});
			const normalizedDataUrl = await this.normalizeAiResultToEditorCanvas(result.dataUrl || result.url, aiPayload.restore);

			this.applyPreviewImageUrl(normalizedDataUrl, {
				aiEditing: false,
				aiError: "",
				hasAiEdits: true
			});
		} catch (err) {
			this.setState({
				aiEditing: false,
				aiError: err && err.message ? err.message : `${err}`
			});
		}
	}

	handleRevertAiEdit() {
		if (!this.state.originalPreviewImageUrl) {
			return;
		}

		this.applyPreviewImageUrl(this.state.originalPreviewImageUrl, {
			aiError: "",
			hasAiEdits: false
		});
	}

	handleImageError() {
		if (this.state.previewImageUrl && this.state.previewImageUrl !== this.props.imageUrl) {
			this.revokePreviewObjectUrl();
			this.setState({
				previewImageUrl: this.props.imageUrl || "",
				previewImageLoading: false,
				sourceReady: false,
				previewExportSafe: isCanvasSafeUrl(this.props.imageUrl || ""),
				error: isCanvasSafeUrl(this.props.imageUrl || "")
					? ""
					: "The image preview loaded, but the browser blocked export access to the source image. Save is disabled."
			});
			return;
		}

		this.setState({
			previewImageLoading: false,
			sourceReady: false,
			error: "The image preview could not be loaded."
		});
	}

	getAspectRatioForPreset(cropPreset = this.state.cropPreset) {
		const preset = CROP_PRESET_OPTIONS.find((option) => option.value === cropPreset);
		return preset ? preset.aspectRatio : null;
	}

	getCropMetrics(overrides = {}) {
		const {
			sourceImageWidth = this.state.sourceImageWidth,
			sourceImageHeight = this.state.sourceImageHeight,
			cropViewportWidth = this.state.cropViewportWidth,
			cropViewportHeight = this.state.cropViewportHeight,
			cropPreset = this.state.cropPreset,
			cropZoom = this.state.cropZoom,
			cropOffsetX = this.state.cropOffsetX,
			cropOffsetY = this.state.cropOffsetY,
			freeCropWidthPct = this.state.freeCropWidthPct,
			freeCropHeightPct = this.state.freeCropHeightPct
		} = overrides;

		if (!sourceImageWidth || !sourceImageHeight || !cropViewportWidth || !cropViewportHeight) {
			return null;
		}

		const aspectRatio = this.getAspectRatioForPreset(cropPreset);
		let viewportWidth = cropViewportWidth;
		let viewportHeight = cropViewportHeight;

		if (aspectRatio) {
			if (cropViewportWidth / cropViewportHeight > aspectRatio) {
				viewportHeight = cropViewportHeight;
				viewportWidth = viewportHeight * aspectRatio;
			} else {
				viewportWidth = cropViewportWidth;
				viewportHeight = viewportWidth / aspectRatio;
			}
		} else {
			const imageAspectRatio = sourceImageWidth / sourceImageHeight;
			let fitWidth = cropViewportWidth;
			let fitHeight = cropViewportHeight;
			if (cropViewportWidth / cropViewportHeight > imageAspectRatio) {
				fitHeight = cropViewportHeight;
				fitWidth = fitHeight * imageAspectRatio;
			} else {
				fitWidth = cropViewportWidth;
				fitHeight = fitWidth / imageAspectRatio;
			}

			viewportWidth = fitWidth * clampValue(freeCropWidthPct, MIN_FREE_CROP_SCALE, 1);
			viewportHeight = fitHeight * clampValue(freeCropHeightPct, MIN_FREE_CROP_SCALE, 1);
		}

		const zoom = clampValue(cropZoom, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const scale = Math.max(
			viewportWidth / sourceImageWidth,
			viewportHeight / sourceImageHeight
		) * zoom;
		const displayWidth = sourceImageWidth * scale;
		const displayHeight = sourceImageHeight * scale;
		const maxOffsetX = Math.max((displayWidth - viewportWidth) / 2, 0);
		const maxOffsetY = Math.max((displayHeight - viewportHeight) / 2, 0);
		const clampedOffsetX = clampValue(cropOffsetX, -maxOffsetX, maxOffsetX);
		const clampedOffsetY = clampValue(cropOffsetY, -maxOffsetY, maxOffsetY);
		const sourceWidth = clampValue(viewportWidth / scale, 1, sourceImageWidth);
		const sourceHeight = clampValue(viewportHeight / scale, 1, sourceImageHeight);

		return {
			viewportWidth,
			viewportHeight,
			displayWidth,
			displayHeight,
			scale,
			imageLeft: (viewportWidth - displayWidth) / 2 + clampedOffsetX,
			imageTop: (viewportHeight - displayHeight) / 2 + clampedOffsetY,
			clampedOffsetX,
			clampedOffsetY,
			sourceImageWidth,
			sourceImageHeight,
			sourceWidth,
			sourceHeight
		};
	}

	getCurrentCropSourceDimensions(metrics = this.getCropMetrics()) {
		if (!metrics) {
			return {
				width: 0,
				height: 0
			};
		}

		return {
			width: Math.max(1, Math.round(metrics.sourceWidth)),
			height: Math.max(1, Math.round(metrics.sourceHeight))
		};
	}

	handleChangeCropPreset(cropPreset) {
		this.setState({
			cropPreset,
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropZoomChange(event) {
		const cropZoom = clampValue(parseFloat(event && event.target ? event.target.value : DEFAULT_CROP_ZOOM) || DEFAULT_CROP_ZOOM, DEFAULT_CROP_ZOOM, MAX_CROP_ZOOM);
		const nextMetrics = this.getCropMetrics({
			cropZoom
		});

		this.setState({
			cropZoom,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropWidthChange(event) {
		const freeCropWidthPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropWidthPct
		});

		this.setState({
			freeCropWidthPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleFreeCropHeightChange(event) {
		const freeCropHeightPct = clampValue(parseFloat(event && event.target ? event.target.value : 1) || 1, MIN_FREE_CROP_SCALE, 1);
		const nextMetrics = this.getCropMetrics({
			freeCropHeightPct
		});

		this.setState({
			freeCropHeightPct,
			cropOffsetX: nextMetrics ? nextMetrics.clampedOffsetX : 0,
			cropOffsetY: nextMetrics ? nextMetrics.clampedOffsetY : 0
		});
	}

	handleResetCrop() {
		this.setState({
			cropZoom: DEFAULT_CROP_ZOOM,
			cropOffsetX: 0,
			cropOffsetY: 0,
			freeCropWidthPct: 1,
			freeCropHeightPct: 1
		});
	}

	handleCropDragStart(event) {
		if (!this.state.sourceReady) {
			return;
		}
		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const metrics = this.getCropMetrics();
		if (!metrics) {
			return;
		}

		event.preventDefault();
		this.dragState = {
			startX: event.clientX,
			startY: event.clientY,
			originOffsetX: metrics.clampedOffsetX,
			originOffsetY: metrics.clampedOffsetY
		};

		window.addEventListener("mousemove", this.handleWindowMouseMove);
		window.addEventListener("mouseup", this.handleWindowMouseUp);
		this.setState({
			isDraggingCrop: true
		});
	}

	handleWindowMouseMove(event) {
		if (!this.dragState) {
			return;
		}

		const nextMetrics = this.getCropMetrics({
			cropOffsetX: this.dragState.originOffsetX + (event.clientX - this.dragState.startX),
			cropOffsetY: this.dragState.originOffsetY + (event.clientY - this.dragState.startY)
		});
		if (!nextMetrics) {
			return;
		}

		this.setState({
			cropOffsetX: nextMetrics.clampedOffsetX,
			cropOffsetY: nextMetrics.clampedOffsetY
		});
	}

	handleWindowMouseUp() {
		this.detachCropDragListeners();
	}

	detachCropDragListeners() {
		window.removeEventListener("mousemove", this.handleWindowMouseMove);
		window.removeEventListener("mouseup", this.handleWindowMouseUp);
		this.dragState = null;
		if (this.state.isDraggingCrop) {
			this.setState({
				isDraggingCrop: false
			});
		}
	}

	handleToggleResizeEnabled(event) {
		const resizeEnabled = !!(event && event.target ? event.target.checked : false);
		if (!resizeEnabled) {
			this.setState({
				resizeEnabled: false
			});
			return;
		}

		const cropDimensions = this.getCurrentCropSourceDimensions();
		this.setState((prevState) => ({
			resizeEnabled: true,
			resizeWidth: prevState.resizeWidth || `${cropDimensions.width || ""}`,
			resizeHeight: prevState.resizeHeight || `${cropDimensions.height || ""}`
		}));
	}

	handleToggleResizeLock(event) {
		this.setState({
			resizeLockAspect: !!(event && event.target ? event.target.checked : false)
		});
	}

	getResizeAspectRatio() {
		const cropDimensions = this.getCurrentCropSourceDimensions();
		if (!cropDimensions.width || !cropDimensions.height) {
			return 1;
		}
		return cropDimensions.width / cropDimensions.height;
	}

	handleResizeWidthChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(parseInt(rawValue, 10) || 0, 1)}`;
		if (!this.state.resizeLockAspect || numericValue === "") {
			this.setState({
				resizeWidth: numericValue
			});
			return;
		}

		const aspectRatio = this.getResizeAspectRatio();
		const widthValue = parseInt(numericValue, 10) || 1;
		this.setState({
			resizeWidth: `${widthValue}`,
			resizeHeight: `${Math.max(Math.round(widthValue / aspectRatio), 1)}`
		});
	}

	handleResizeHeightChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(parseInt(rawValue, 10) || 0, 1)}`;
		if (!this.state.resizeLockAspect || numericValue === "") {
			this.setState({
				resizeHeight: numericValue
			});
			return;
		}

		const aspectRatio = this.getResizeAspectRatio();
		const heightValue = parseInt(numericValue, 10) || 1;
		this.setState({
			resizeHeight: `${heightValue}`,
			resizeWidth: `${Math.max(Math.round(heightValue * aspectRatio), 1)}`
		});
	}

	handleBlurChange(event) {
		this.setState({
			blurPx: clampValue(parseFloat(event && event.target ? event.target.value : 0) || 0, 0, MAX_BLUR_PX)
		});
	}

	handlePaddingChange(event) {
		this.setState({
			paddingPx: clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, MAX_PADDING_INPUT_PX)
		});
	}

	handleTogglePadToTargetSize(event) {
		const padToTargetSize = !!(event && event.target ? event.target.checked : false);
		if (!padToTargetSize) {
			this.setState({
				padToTargetSize: false
			});
			return;
		}

		const cropDimensions = this.getCurrentCropSourceDimensions();
		const outputContentDimensions = this.getOutputContentDimensions(cropDimensions);
		const currentFrameConfig = this.getOutputFrameConfig(outputContentDimensions);

		this.setState((prevState) => ({
			padToTargetSize: true,
			padTargetWidth: prevState.padTargetWidth || `${currentFrameConfig.width || outputContentDimensions.width || ""}`,
			padTargetHeight: prevState.padTargetHeight || `${currentFrameConfig.height || outputContentDimensions.height || ""}`
		}));
	}

	handlePadTargetWidthChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(Math.min(parseInt(rawValue, 10) || 0, MAX_FINAL_OUTPUT_DIMENSION_PX), 1)}`;
		this.setState({
			padTargetWidth: numericValue
		});
	}

	handlePadTargetHeightChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const numericValue = rawValue === "" ? "" : `${Math.max(Math.min(parseInt(rawValue, 10) || 0, MAX_FINAL_OUTPUT_DIMENSION_PX), 1)}`;
		this.setState({
			padTargetHeight: numericValue
		});
	}

	drawSourceCropCanvas() {
		const image = this.getImageElement();
		const canvas = this.getCanvasElement();
		const metrics = this.getCropMetrics();
		if (!image || !canvas || !metrics) {
			throw new Error("Image preview is not ready.");
		}
		if (!image.naturalWidth || !image.naturalHeight) {
			throw new Error("Image has not loaded enough data yet.");
		}

		const sourceX = clampValue((-metrics.imageLeft) / metrics.scale, 0, metrics.sourceImageWidth - 1);
		const sourceY = clampValue((-metrics.imageTop) / metrics.scale, 0, metrics.sourceImageHeight - 1);
		const sourceWidth = clampValue(metrics.viewportWidth / metrics.scale, 1, metrics.sourceImageWidth - sourceX);
		const sourceHeight = clampValue(metrics.viewportHeight / metrics.scale, 1, metrics.sourceImageHeight - sourceY);

		canvas.width = Math.max(1, Math.round(sourceWidth));
		canvas.height = Math.max(1, Math.round(sourceHeight));
		const context = canvas.getContext("2d");
		context.clearRect(0, 0, canvas.width, canvas.height);
		context.drawImage(
			image,
			sourceX,
			sourceY,
			sourceWidth,
			sourceHeight,
			0,
			0,
			canvas.width,
			canvas.height
		);
		return canvas;
	}

	getOutputContentDimensions(sourceCanvas) {
		const fallbackWidth = sourceCanvas ? sourceCanvas.width : 1;
		const fallbackHeight = sourceCanvas ? sourceCanvas.height : 1;

		if (!this.state.resizeEnabled) {
			return {
				width: fallbackWidth,
				height: fallbackHeight
			};
		}

		const widthValue = Math.max(parseInt(this.state.resizeWidth, 10) || fallbackWidth, 1);
		const heightValue = Math.max(parseInt(this.state.resizeHeight, 10) || fallbackHeight, 1);
		return {
			width: widthValue,
			height: heightValue
		};
	}

	getOutputFrameConfig(contentDimensions = {}) {
		const contentWidth = Math.max(parseInt(contentDimensions.width, 10) || 1, 1);
		const contentHeight = Math.max(parseInt(contentDimensions.height, 10) || 1, 1);

		if (this.state.padToTargetSize) {
			const frameWidth = Math.max(
				Math.min(parseInt(this.state.padTargetWidth, 10) || contentWidth, MAX_FINAL_OUTPUT_DIMENSION_PX),
				contentWidth
			);
			const frameHeight = Math.max(
				Math.min(parseInt(this.state.padTargetHeight, 10) || contentHeight, MAX_FINAL_OUTPUT_DIMENSION_PX),
				contentHeight
			);

			return {
				width: frameWidth,
				height: frameHeight,
				offsetX: Math.max(Math.floor((frameWidth - contentWidth) / 2), 0),
				offsetY: Math.max(Math.floor((frameHeight - contentHeight) / 2), 0),
				mode: "target"
			};
		}

		const paddingPx = clampValue(parseInt(this.state.paddingPx, 10) || 0, 0, MAX_PADDING_INPUT_PX);
		return {
			width: Math.max(contentWidth + (paddingPx * 2), 1),
			height: Math.max(contentHeight + (paddingPx * 2), 1),
			offsetX: paddingPx,
			offsetY: paddingPx,
			paddingPx,
			mode: "uniform"
		};
	}

	createOutputCanvas() {
		const sourceCanvas = this.drawSourceCropCanvas();
		const { width: contentWidth, height: contentHeight } = this.getOutputContentDimensions(sourceCanvas);
		const blurPx = clampValue(parseFloat(this.state.blurPx) || 0, 0, MAX_BLUR_PX);
		const outputFrameConfig = this.getOutputFrameConfig({ width: contentWidth, height: contentHeight });

		const outputCanvas = document.createElement("canvas");
		outputCanvas.width = Math.max(outputFrameConfig.width || contentWidth, 1);
		outputCanvas.height = Math.max(outputFrameConfig.height || contentHeight, 1);
		const outputContext = outputCanvas.getContext("2d");
		outputContext.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
		outputContext.filter = blurPx > 0 ? `blur(${blurPx}px)` : "none";
		outputContext.drawImage(
			sourceCanvas,
			outputFrameConfig.offsetX || 0,
			outputFrameConfig.offsetY || 0,
			contentWidth,
			contentHeight
		);
		outputContext.filter = "none";
		return outputCanvas;
	}

	createAiEditPayload() {
		const outputCanvas = this.createOutputCanvas();
		const originalWidth = outputCanvas.width;
		const originalHeight = outputCanvas.height;
		const aiSide = getBestAiSquareSize(Math.max(originalWidth, originalHeight));
		const scale = Math.min(aiSide / originalWidth, aiSide / originalHeight);
		const placedWidth = Math.max(1, Math.round(originalWidth * scale));
		const placedHeight = Math.max(1, Math.round(originalHeight * scale));
		const offsetX = Math.round((aiSide - placedWidth) / 2);
		const offsetY = Math.round((aiSide - placedHeight) / 2);

		const aiCanvas = document.createElement("canvas");
		aiCanvas.width = aiSide;
		aiCanvas.height = aiSide;
		const aiContext = aiCanvas.getContext("2d");
		aiContext.clearRect(0, 0, aiSide, aiSide);
		aiContext.drawImage(outputCanvas, 0, 0, originalWidth, originalHeight, offsetX, offsetY, placedWidth, placedHeight);

		const imageData = outputCanvas.getContext("2d").getImageData(0, 0, originalWidth, originalHeight).data;
		let hasTransparency = false;
		for (let i = 3; i < imageData.length; i += 4) {
			if (imageData[i] < 250) {
				hasTransparency = true;
				break;
			}
		}

		let maskCanvas = null;
		if (hasTransparency) {
			maskCanvas = document.createElement("canvas");
			maskCanvas.width = aiSide;
			maskCanvas.height = aiSide;
			const maskContext = maskCanvas.getContext("2d");
			maskContext.clearRect(0, 0, aiSide, aiSide);
			maskContext.fillStyle = "#ffffff";
			maskContext.fillRect(offsetX, offsetY, placedWidth, placedHeight);
			maskContext.globalCompositeOperation = "destination-in";
			maskContext.drawImage(outputCanvas, 0, 0, originalWidth, originalHeight, offsetX, offsetY, placedWidth, placedHeight);
			maskContext.globalCompositeOperation = "source-over";
		}

		return {
			imageCanvas: aiCanvas,
			maskCanvas,
			size: `${aiSide}x${aiSide}`,
			restore: {
				offsetX,
				offsetY,
				placedWidth,
				placedHeight,
				originalWidth,
				originalHeight
			}
		};
	}

	async normalizeAiResultToEditorCanvas(resultDataUrl, restore = {}) {
		const {
			offsetX = 0,
			offsetY = 0,
			placedWidth = 0,
			placedHeight = 0,
			originalWidth = 0,
			originalHeight = 0
		} = restore || {};

		if (!resultDataUrl || !originalWidth || !originalHeight || !placedWidth || !placedHeight) {
			return resultDataUrl;
		}

		const image = await new Promise((resolve, reject) => {
			const nextImage = new window.Image();
			nextImage.onload = () => resolve(nextImage);
			nextImage.onerror = () => reject(new Error("Edited image could not be loaded."));
			nextImage.src = resultDataUrl;
		});

		const normalizedCanvas = document.createElement("canvas");
		normalizedCanvas.width = originalWidth;
		normalizedCanvas.height = originalHeight;
		const normalizedContext = normalizedCanvas.getContext("2d");
		normalizedContext.clearRect(0, 0, originalWidth, originalHeight);
		normalizedContext.drawImage(
			image,
			offsetX,
			offsetY,
			placedWidth,
			placedHeight,
			0,
			0,
			originalWidth,
			originalHeight
		);

		return normalizedCanvas.toDataURL("image/png");
	}

	canvasToBlob(canvas) {
		return new Promise((resolve, reject) => {
			canvas.toBlob((blob) => {
				if (blob) {
					resolve(blob);
				} else {
					reject(new Error("Unable to create edited image blob."));
				}
			}, "image/png");
		});
	}

	handleOpenMarkupModal() {
		if (!this.state.sourceReady || this.state.previewImageLoading) {
			this.setState({
				error: "Wait for the image preview to finish loading before using markup."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				error: "This image preview is not yet canvas-safe, so markup is unavailable until the secure preview source finishes loading."
			});
			return;
		}

		try {
			const outputCanvas = this.createOutputCanvas();
			const markupSourceUrl = outputCanvas.toDataURL("image/png");
			this.setState({
				showMarkupModal: true,
				markupSourceUrl,
				markupSourceImageSize: {
					width: outputCanvas.width,
					height: outputCanvas.height
				},
				markupItems: [],
				selectedMarkupGuid: "",
				markupMouseX: 0,
				markupMouseY: 0,
				markupMouseDown: false,
				markupStartX: 0,
				markupStartY: 0,
				markupCurrentX: 0,
				markupCurrentY: 0,
				markupRotation: 0,
				error: ""
			});
		} catch (err) {
			this.setState({
				error: err && err.message ? err.message : `${err}`
			});
		}
	}

	handleCloseMarkupModal() {
		this.stopMarkupPressAndHold();
		this.detachMarkupPointerListeners();
		this.setState({
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupMouseDown: false,
			markupRotation: 0
		});
	}

	handleApplyMarkupComposite(resultDataUrl) {
		if (!resultDataUrl) {
			this.handleCloseMarkupModal();
			return;
		}

		this.stopMarkupPressAndHold();
		this.detachMarkupPointerListeners();
		this.setState({
			showMarkupModal: false,
			markupSourceUrl: "",
			markupSourceImageSize: null,
			markupItems: [],
			selectedMarkupGuid: "",
			markupMouseDown: false,
			markupRotation: 0
		}, () => {
			this.applyPreviewImageUrl(resultDataUrl, {
				error: ""
			});
		});
	}

	getMarkupPointerPosition(event) {
		const { markupSourceImageSize } = this.state;
		const svg = this.markupSvgRef.current;
		if (!svg || !markupSourceImageSize) {
			return null;
		}

		const rect = svg.getBoundingClientRect();
		if (!rect.width || !rect.height) {
			return null;
		}

		const x = clampValue(
			((event.clientX - rect.left) / rect.width) * markupSourceImageSize.width,
			0,
			markupSourceImageSize.width
		);
		const y = clampValue(
			((event.clientY - rect.top) / rect.height) * markupSourceImageSize.height,
			0,
			markupSourceImageSize.height
		);

		return {
			x: Math.round(x),
			y: Math.round(y)
		};
	}

	getSelectedMarkupItem() {
		return this.state.markupItems.find((item) => item.guid === this.state.selectedMarkupGuid) || null;
	}

	getMarkupFillColor() {
		if (!this.state.markupFillEnabled) {
			return "transparent";
		}
		return this.state.markupFillColor || "transparent";
	}

	getNextMarkupId(componentType = "highlight", shape = "rectangle") {
		const shapeBase = shape === "arrow" ? "arrow" : componentType;
		const sameTypeCount = this.state.markupItems.filter((item) => {
			if (shape === "arrow") {
				return item.shape === "arrow";
			}
			return item.type === componentType && item.shape !== "arrow";
		}).length;

		return `${shapeBase}${sameTypeCount + 1}`;
	}

	createMarkupItemFromBounds(startX, startY, endX, endY) {
		const x = Math.round(Math.min(startX, endX));
		const y = Math.round(Math.min(startY, endY));
		const width = Math.round(Math.abs(endX - startX));
		const height = Math.round(Math.abs(endY - startY));

		if (width < 2 || height < 2) {
			return null;
		}

		const { markupComponentType, markupShape } = this.state;
		return {
			guid: createMarkupGuid(),
			id: this.getNextMarkupId(markupComponentType, markupShape),
			type: markupComponentType,
			shape: markupShape,
			visible: true,
			coords: {
				x,
				y,
				width,
				height
			},
			style: {
				borderColor: this.state.markupBorderColor,
				borderWidth: Number(this.state.markupBorderWidth) || 0,
				borderRadius: markupShape === "arrow" ? 0 : (Number(this.state.markupBorderRadius) || 0),
				borderFillColor: this.getMarkupFillColor()
			},
			rotation: 0
		};
	}

	applyMarkupSelectionToControls(markupItem) {
		if (!markupItem) {
			return;
		}

		const fillColor = markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		this.setState({
			selectedMarkupGuid: markupItem.guid,
			markupComponentType: markupItem.type || "highlight",
			markupShape: markupItem.shape || "rectangle",
			markupBorderColor: (markupItem.style && markupItem.style.borderColor) || getMarkupDefaults(markupItem.type || "highlight").borderColor,
			markupBorderWidth: Number(markupItem.style && markupItem.style.borderWidth) || 0,
			markupBorderRadius: Number(markupItem.style && markupItem.style.borderRadius) || 0,
			markupFillEnabled: !isTransparentMarkupColor(fillColor),
			markupFillColor: !isTransparentMarkupColor(fillColor) ? fillColor : this.state.markupFillColor,
			markupRotation: normalizeMarkupRotation(markupItem.rotation || 0)
		});
	}

	updateSelectedMarkupItem(updater) {
		const { selectedMarkupGuid, markupItems } = this.state;
		if (!selectedMarkupGuid) {
			return;
		}

		const nextItems = markupItems.map((item) => {
			if (item.guid !== selectedMarkupGuid) {
				return item;
			}
			return updater(item);
		});

		this.setState({
			markupItems: nextItems
		});
	}

	toggleMarkupVisibility(guid) {
		this.setState((prevState) => ({
			markupItems: prevState.markupItems.map((item) => {
				if (item.guid !== guid) {
					return item;
				}

				return {
					...item,
					visible: item.visible !== false ? false : true
				};
			})
		}));
	}

	handleMarkupComponentTypeChange(event) {
		const markupComponentType = event && event.target ? event.target.value : "highlight";
		const defaults = getMarkupDefaults(markupComponentType);
		this.setState({
			markupComponentType,
			markupShape: defaults.shape,
			markupBorderColor: defaults.borderColor,
			markupBorderWidth: defaults.borderWidth,
			markupBorderRadius: defaults.borderRadius,
			markupFillEnabled: false,
			markupFillColor: defaults.fillColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				type: markupComponentType
			}));
		});
	}

	handleMarkupShapeChange(event) {
		const markupShape = event && event.target ? event.target.value : "rectangle";
		this.setState({
			markupShape,
			markupBorderRadius: markupShape === "arrow" ? 0 : this.state.markupBorderRadius
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				shape: markupShape,
				style: {
					...item.style,
					borderRadius: markupShape === "arrow" ? 0 : item.style.borderRadius
				}
			}));
		});
	}

	handleMarkupBorderColorChange(colorResult) {
		const markupBorderColor = colorResultToRgba(colorResult, this.state.markupBorderColor);
		this.setState({
			markupBorderColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderColor: markupBorderColor
				}
			}));
		});
	}

	handleMarkupBorderWidthChange(event) {
		const markupBorderWidth = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 16);
		this.setState({
			markupBorderWidth
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderWidth: markupBorderWidth
				}
			}));
		});
	}

	handleMarkupBorderRadiusChange(event) {
		const markupBorderRadius = clampValue(parseInt(event && event.target ? event.target.value : 0, 10) || 0, 0, 64);
		this.setState({
			markupBorderRadius
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderRadius: item.shape === "arrow" ? 0 : markupBorderRadius
				}
			}));
		});
	}

	handleMarkupFillEnabledChange(event) {
		const markupFillEnabled = !!(event && event.target ? event.target.checked : false);
		this.setState({
			markupFillEnabled
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderFillColor: markupFillEnabled
						? (this.state.markupFillColor || item.style.borderColor || this.state.markupBorderColor)
						: "transparent"
				}
			}));
		});
	}

	handleMarkupFillColorChange(colorResult) {
		const markupFillColor = colorResultToRgba(colorResult, this.state.markupFillColor);
		this.setState({
			markupFillColor
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				style: {
					...item.style,
					borderFillColor: this.state.markupFillEnabled
						? markupFillColor
						: "transparent"
				}
			}));
		});
	}

	handleMarkupRotationChange(event) {
		const rawValue = event && event.target ? event.target.value : "";
		const markupRotation = rawValue === "" ? 0 : normalizeMarkupRotation(parseFloat(rawValue));
		this.setState({
			markupRotation
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				rotation: markupRotation
			}));
		});
	}

	handleRotateSelectedMarkup(deltaDegrees = 0) {
		const markupRotation = normalizeMarkupRotation((this.state.markupRotation || 0) + deltaDegrees);
		this.setState({
			markupRotation
		}, () => {
			this.updateSelectedMarkupItem((item) => ({
				...item,
				rotation: markupRotation
			}));
		});
	}

	handleMarkupToggleLabels() {
		this.setState((prevState) => ({
			markupShowItemIds: !prevState.markupShowItemIds
		}));
	}

	handleMarkupPreviewScaleChange(event) {
		const markupPreviewScale = clampValue(parseInt(event && event.target ? event.target.value : 100, 10) || 100, 25, 200);
		this.setState({
			markupPreviewScale
		});
	}

	handleMarkupMouseDown(event) {
		if (event && typeof event.button === "number" && event.button !== 0) {
			return;
		}

		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.setState({
			selectedMarkupGuid: "",
			markupMouseDown: true,
			markupStartX: point.x,
			markupStartY: point.y,
			markupCurrentX: point.x,
			markupCurrentY: point.y,
			markupMouseX: point.x,
			markupMouseY: point.y
		});
	}

	handleMarkupMouseMove(event) {
		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		if (this.state.markupMouseDown) {
			this.setState({
				markupCurrentX: point.x,
				markupCurrentY: point.y,
				markupMouseX: point.x,
				markupMouseY: point.y
			});
			return;
		}

		this.setState({
			markupMouseX: point.x,
			markupMouseY: point.y
		});
	}

	handleMarkupMouseUp(event) {
		if (!this.state.markupMouseDown) {
			return;
		}

		const point = this.getMarkupPointerPosition(event) || {
			x: this.state.markupCurrentX,
			y: this.state.markupCurrentY
		};

		const nextItem = this.createMarkupItemFromBounds(
			this.state.markupStartX,
			this.state.markupStartY,
			point.x,
			point.y
		);

		if (!nextItem) {
			this.setState({
				markupMouseDown: false
			});
			return;
		}

		this.setState((prevState) => ({
			markupItems: [...prevState.markupItems, nextItem],
			selectedMarkupGuid: nextItem.guid,
			markupMouseDown: false,
			markupMouseX: point.x,
			markupMouseY: point.y,
			markupCurrentX: point.x,
			markupCurrentY: point.y
		}), () => {
			this.applyMarkupSelectionToControls(nextItem);
		});
	}

	handleMarkupCanvasClick() {
		if (this.state.markupMouseDown) {
			return;
		}

		this.setState({
			selectedMarkupGuid: "",
			markupRotation: 0
		});
	}

	handleSelectMarkupItem(guid) {
		const markupItem = this.state.markupItems.find((item) => item.guid === guid);
		if (!markupItem) {
			return;
		}
		this.applyMarkupSelectionToControls(markupItem);
	}

	handleDeleteMarkupItem(guid) {
		this.setState((prevState) => ({
			markupItems: prevState.markupItems.filter((item) => item.guid !== guid),
			selectedMarkupGuid: prevState.selectedMarkupGuid === guid ? "" : prevState.selectedMarkupGuid,
			markupRotation: prevState.selectedMarkupGuid === guid ? 0 : prevState.markupRotation
		}));
	}

	handleDeleteSelectedMarkupItem() {
		if (!this.state.selectedMarkupGuid) {
			return;
		}
		this.handleDeleteMarkupItem(this.state.selectedMarkupGuid);
	}

	runMarkupPressAndHold(action) {
		this.markupPressAndHoldCount += 1;
		const nextStepSize = this.markupPressAndHoldCount > 50 ? 20 : this.markupPressAndHoldCount > 5 ? 5 : 1;
		action(nextStepSize);
		const nextDelay = this.markupPressAndHoldCount > 5 ? 50 : 200;
		this.markupPressAndHoldTimer = window.setTimeout(() => {
			this.runMarkupPressAndHold(action);
		}, nextDelay);
	}

	startMarkupPressAndHold(action) {
		this.stopMarkupPressAndHold();
		this.markupPressAndHoldCount = 0;
		this.markupPressAndHoldTimer = window.setTimeout(() => {
			this.runMarkupPressAndHold(action);
		}, 200);
		window.addEventListener("mouseup", this.stopMarkupPressAndHold);
	}

	stopMarkupPressAndHold() {
		if (this.markupPressAndHoldTimer) {
			window.clearTimeout(this.markupPressAndHoldTimer);
			this.markupPressAndHoldTimer = null;
		}
		this.markupPressAndHoldCount = 0;
		window.removeEventListener("mouseup", this.stopMarkupPressAndHold);
	}

	handleToggleMarkupVisibility(guid, event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.toggleMarkupVisibility(guid);
	}

	handleToggleSelectedMarkupVisibility() {
		if (!this.state.selectedMarkupGuid) {
			return;
		}
		this.toggleMarkupVisibility(this.state.selectedMarkupGuid);
	}

	handleCloneSelectedMarkup() {
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem) {
			return;
		}

		const clonedMarkupItem = {
			...selectedMarkupItem,
			guid: createMarkupGuid(),
			id: this.getNextMarkupId(selectedMarkupItem.type || "highlight", selectedMarkupItem.shape || "rectangle"),
			visible: true,
			coords: {
				...selectedMarkupItem.coords,
				x: selectedMarkupItem.coords.x + 24,
				y: selectedMarkupItem.coords.y + 24
			}
		};

		this.setState((prevState) => ({
			markupItems: [...prevState.markupItems, clonedMarkupItem],
			selectedMarkupGuid: clonedMarkupItem.guid,
			markupRotation: normalizeMarkupRotation(clonedMarkupItem.rotation || 0)
		}), () => {
			this.applyMarkupSelectionToControls(clonedMarkupItem);
		});
	}

	handleNudgeSelectedMarkup(deltaX = 0, deltaY = 0) {
		const { markupSourceImageSize } = this.state;
		const selectedMarkupItem = this.getSelectedMarkupItem();
		if (!selectedMarkupItem || !markupSourceImageSize) {
			return;
		}

		const nextX = clampValue(
			(selectedMarkupItem.coords.x || 0) + deltaX,
			0,
			Math.max(markupSourceImageSize.width - selectedMarkupItem.coords.width, 0)
		);
		const nextY = clampValue(
			(selectedMarkupItem.coords.y || 0) + deltaY,
			0,
			Math.max(markupSourceImageSize.height - selectedMarkupItem.coords.height, 0)
		);

		this.updateSelectedMarkupItem((item) => ({
			...item,
			coords: {
				...item.coords,
				x: nextX,
				y: nextY
			}
		}));
	}

	startMarkupMoveDrag(markupItem, event) {
		const point = this.getMarkupPointerPosition(event);
		if (!markupItem || !point) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.markupPointerDrag = {
			type: "move",
			guid: markupItem.guid,
			startPoint: point,
			startCoords: { ...markupItem.coords }
		};
		this.attachMarkupPointerListeners();
	}

	startMarkupRotateDrag(markupItem, event) {
		const point = this.getMarkupPointerPosition(event);
		if (!markupItem || !point) {
			return;
		}

		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);
		const startAngle = Math.atan2(point.y - centerY, point.x - centerX);

		event.preventDefault();
		event.stopPropagation();
		this.markupPointerDrag = {
			type: "rotate",
			guid: markupItem.guid,
			centerX,
			centerY,
			startAngle,
			startRotation: normalizeMarkupRotation(markupItem.rotation || 0)
		};
		this.attachMarkupPointerListeners();
	}

	attachMarkupPointerListeners() {
		window.addEventListener("mousemove", this.handleWindowMarkupMouseMove);
		window.addEventListener("mouseup", this.handleWindowMarkupMouseUp);
	}

	detachMarkupPointerListeners() {
		window.removeEventListener("mousemove", this.handleWindowMarkupMouseMove);
		window.removeEventListener("mouseup", this.handleWindowMarkupMouseUp);
		this.markupPointerDrag = null;
	}

	handleWindowMarkupMouseMove(event) {
		const drag = this.markupPointerDrag;
		if (!drag) {
			return;
		}

		const point = this.getMarkupPointerPosition(event);
		if (!point) {
			return;
		}

		this.setState({
			markupMouseX: point.x,
			markupMouseY: point.y
		});

		if (drag.type === "move") {
			const { markupSourceImageSize } = this.state;
			if (!markupSourceImageSize) {
				return;
			}
			const selectedMarkupItem = this.state.markupItems.find((item) => item.guid === drag.guid);
			if (!selectedMarkupItem) {
				return;
			}

			const deltaX = point.x - drag.startPoint.x;
			const deltaY = point.y - drag.startPoint.y;
			const nextX = clampValue(
				drag.startCoords.x + deltaX,
				0,
				Math.max(markupSourceImageSize.width - selectedMarkupItem.coords.width, 0)
			);
			const nextY = clampValue(
				drag.startCoords.y + deltaY,
				0,
				Math.max(markupSourceImageSize.height - selectedMarkupItem.coords.height, 0)
			);

			this.setState((prevState) => ({
				markupItems: prevState.markupItems.map((item) => {
					if (item.guid !== drag.guid) {
						return item;
					}

					return {
						...item,
						coords: {
							...item.coords,
							x: nextX,
							y: nextY
						}
					};
				})
			}));
			return;
		}

		if (drag.type === "rotate") {
			const currentAngle = Math.atan2(point.y - drag.centerY, point.x - drag.centerX);
			const nextRotation = normalizeMarkupRotation(drag.startRotation + ((currentAngle - drag.startAngle) * 180 / Math.PI));
			this.setState((prevState) => ({
				markupRotation: nextRotation,
				markupItems: prevState.markupItems.map((item) => {
					if (item.guid !== drag.guid) {
						return item;
					}

					return {
						...item,
						rotation: nextRotation
					};
				})
			}));
		}
	}

	handleWindowMarkupMouseUp() {
		this.detachMarkupPointerListeners();
	}

	getMarkupStrokeConfig(markupItem = {}) {
		let strokeWidth = Number(markupItem.style && markupItem.style.borderWidth);
		const stroke = markupItem.style && markupItem.style.borderColor ? markupItem.style.borderColor : "#2563eb";
		let strokeDasharray;
		if (!Number.isFinite(strokeWidth)) {
			strokeWidth = 1;
		}
		if (strokeWidth <= 0 || isTransparentMarkupColor(stroke)) {
			strokeWidth = 1;
			strokeDasharray = "10,10";
		}
		return {
			strokeWidth,
			stroke,
			strokeDasharray
		};
	}

	getMarkupTempItem() {
		if (!this.state.markupMouseDown) {
			return null;
		}

		return this.createMarkupItemFromBounds(
			this.state.markupStartX,
			this.state.markupStartY,
			this.state.markupCurrentX,
			this.state.markupCurrentY
		);
	}

	buildArrowPolygonPoints(coords = {}) {
		const x = coords.x || 0;
		const y = coords.y || 0;
		const width = coords.width || 0;
		const height = coords.height || 0;
		return `${x},${y + (height * 0.33)} ${x + (width * 0.5)},${y + (height * 0.33)} ${x + (width * 0.5)},${y} ${x + width},${y + (height * 0.5)} ${x + (width * 0.5)},${y + height} ${x + (width * 0.5)},${y + (height * 0.67)} ${x},${y + (height * 0.67)}`;
	}

	renderMarkupSvgItem(markupItem, { isDraft = false } = {}) {
		if (!markupItem || !markupItem.coords) {
			return null;
		}
		if (!isDraft && markupItem.visible === false) {
			return null;
		}

		const { strokeWidth, stroke, strokeDasharray } = this.getMarkupStrokeConfig(markupItem);
		const fill = markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		const isSelected = !isDraft && markupItem.guid === this.state.selectedMarkupGuid;
		const rotation = normalizeMarkupRotation(markupItem.rotation || 0);
		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);
		const rotatedTopRight = rotatePointAroundCenter(markupItem.coords.x + markupItem.coords.width, markupItem.coords.y, centerX, centerY, rotation);
		const rotatedTopCenter = rotatePointAroundCenter(markupItem.coords.x + (markupItem.coords.width / 2), markupItem.coords.y, centerX, centerY, rotation);
		const rotatedHandlePoint = rotatePointAroundCenter(markupItem.coords.x + (markupItem.coords.width / 2), markupItem.coords.y - 30, centerX, centerY, rotation);
		const deleteX = rotatedTopRight.x + 10;
		const deleteY = rotatedTopRight.y - 10;
		const commonProps = {
			strokeWidth,
			stroke,
			fill,
			strokeDasharray,
			opacity: isDraft ? 0.8 : 1
		};

		return (
			<g
				key={markupItem.guid || `draft-${markupItem.shape}`}
				onMouseDown={(event) => {
					if (isDraft) {
						return;
					}
					event.preventDefault();
					event.stopPropagation();
					if (isSelected) {
						this.startMarkupMoveDrag(markupItem, event);
					}
				}}
				onClick={(event) => {
					if (isDraft) {
						return;
					}
					event.stopPropagation();
					this.handleSelectMarkupItem(markupItem.guid);
				}}
				style={{ cursor: isDraft ? "crosshair" : "pointer" }}
			>
				<g transform={rotation ? `rotate(${rotation} ${centerX} ${centerY})` : undefined}>
					{markupItem.shape === "arrow" ? (
						<polygon points={this.buildArrowPolygonPoints(markupItem.coords)} {...commonProps} />
					) : (
						<rect
							x={markupItem.coords.x}
							y={markupItem.coords.y}
							width={markupItem.coords.width}
							height={markupItem.coords.height}
							rx={Number(markupItem.style && markupItem.style.borderRadius) || 0}
							{...commonProps}
						/>
					)}
					{this.state.markupShowItemIds && !isDraft && (
						<g pointerEvents="none">
							<rect
								x={markupItem.coords.x + 4}
								y={markupItem.coords.y + markupItem.coords.height - 26}
								width={Math.max(((markupItem.id || "").length * 9) + 10, 54)}
								height={22}
								rx={6}
								fill="rgba(0,0,0,0.88)"
							/>
							<text
								x={markupItem.coords.x + 10}
								y={markupItem.coords.y + markupItem.coords.height - 10}
								fill="#7CFC00"
								fontSize="16"
								fontWeight="bold"
							>
								{markupItem.id}
							</text>
						</g>
					)}
				</g>
				{isSelected && !isDraft && (
					<>
						<line
							x1={rotatedTopCenter.x}
							y1={rotatedTopCenter.y}
							x2={rotatedHandlePoint.x}
							y2={rotatedHandlePoint.y}
							stroke="#2563eb"
							strokeWidth={2}
							strokeDasharray="4,4"
							pointerEvents="none"
						/>
						<circle
							cx={rotatedHandlePoint.x}
							cy={rotatedHandlePoint.y}
							r={11}
							fill="#ffffff"
							stroke="#2563eb"
							strokeWidth={2}
							onMouseDown={(event) => {
								this.startMarkupRotateDrag(markupItem, event);
							}}
						/>
						<text
							x={rotatedHandlePoint.x}
							y={rotatedHandlePoint.y}
							fill="#2563eb"
							fontSize="14"
							fontWeight="bold"
							textAnchor="middle"
							dominantBaseline="central"
							pointerEvents="none"
						>
							↻
						</text>
						<circle
							cx={deleteX}
							cy={deleteY}
							r={11}
							fill="#dc2626"
							stroke="#ffffff"
							strokeWidth={3}
							onMouseDown={(event) => {
								event.preventDefault();
								event.stopPropagation();
							}}
							onMouseUp={(event) => {
								event.preventDefault();
								event.stopPropagation();
							}}
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								this.handleDeleteMarkupItem(markupItem.guid);
							}}
						/>
						<text
							x={deleteX}
							y={deleteY}
							fill="#ffffff"
							fontSize="14"
							fontWeight="bold"
							textAnchor="middle"
							dominantBaseline="central"
							pointerEvents="none"
						>
							×
						</text>
					</>
				)}
			</g>
		);
	}

	drawRoundedRect(context, x, y, width, height, radius = 0) {
		const safeRadius = Math.max(0, Math.min(Number(radius) || 0, Math.min(width, height) / 2));
		context.beginPath();
		context.moveTo(x + safeRadius, y);
		context.lineTo(x + width - safeRadius, y);
		context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
		context.lineTo(x + width, y + height - safeRadius);
		context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
		context.lineTo(x + safeRadius, y + height);
		context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
		context.lineTo(x, y + safeRadius);
		context.quadraticCurveTo(x, y, x + safeRadius, y);
		context.closePath();
	}

	drawArrowPath(context, x, y, width, height) {
		context.beginPath();
		context.moveTo(x, y + (height * 0.33));
		context.lineTo(x + (width * 0.5), y + (height * 0.33));
		context.lineTo(x + (width * 0.5), y);
		context.lineTo(x + width, y + (height * 0.5));
		context.lineTo(x + (width * 0.5), y + height);
		context.lineTo(x + (width * 0.5), y + (height * 0.67));
		context.lineTo(x, y + (height * 0.67));
		context.closePath();
	}

	drawMarkupItemToCanvas(context, markupItem) {
		if (!markupItem || !markupItem.coords) {
			return;
		}
		if (markupItem.visible === false) {
			return;
		}

		const { strokeWidth, stroke, strokeDasharray } = this.getMarkupStrokeConfig(markupItem);
		const fill = markupItem.style && markupItem.style.borderFillColor ? markupItem.style.borderFillColor : "transparent";
		const rotation = normalizeMarkupRotation(markupItem.rotation || 0);
		const centerX = markupItem.coords.x + (markupItem.coords.width / 2);
		const centerY = markupItem.coords.y + (markupItem.coords.height / 2);
		context.save();
		context.lineWidth = strokeWidth;
		context.strokeStyle = stroke;
		context.fillStyle = fill;
		context.setLineDash(strokeDasharray ? strokeDasharray.split(",").map((value) => Number(value) || 0) : []);
		context.translate(centerX, centerY);
		context.rotate((rotation * Math.PI) / 180);
		if (markupItem.shape === "arrow") {
			this.drawArrowPath(context, -(markupItem.coords.width / 2), -(markupItem.coords.height / 2), markupItem.coords.width, markupItem.coords.height);
		} else {
			this.drawRoundedRect(
				context,
				-(markupItem.coords.width / 2),
				-(markupItem.coords.height / 2),
				markupItem.coords.width,
				markupItem.coords.height,
				Number(markupItem.style && markupItem.style.borderRadius) || 0
			);
		}

		if (fill !== "transparent") {
			context.fill();
		}
		context.stroke();
		context.restore();
	}

	async buildMarkupCompositeDataUrl() {
		const image = await new Promise((resolve, reject) => {
			const nextImage = new window.Image();
			nextImage.onload = () => resolve(nextImage);
			nextImage.onerror = () => reject(new Error("The markup image could not be loaded."));
			nextImage.src = this.state.markupSourceUrl;
		});

		const canvas = document.createElement("canvas");
		canvas.width = this.state.markupSourceImageSize ? this.state.markupSourceImageSize.width : (image.naturalWidth || image.width || 1);
		canvas.height = this.state.markupSourceImageSize ? this.state.markupSourceImageSize.height : (image.naturalHeight || image.height || 1);
		const context = canvas.getContext("2d");
		context.clearRect(0, 0, canvas.width, canvas.height);
		context.drawImage(image, 0, 0, canvas.width, canvas.height);
		this.state.markupItems.forEach((item) => this.drawMarkupItemToCanvas(context, item));
		return canvas.toDataURL("image/png");
	}

	async handleApplyInlineMarkup() {
		try {
			const resultDataUrl = await this.buildMarkupCompositeDataUrl();
			this.handleApplyMarkupComposite(resultDataUrl);
		} catch (err) {
			this.setState({
				error: err && err.message ? err.message : `${err}`
			});
		}
	}

	async handleSaveImage() {
		const imageName = `${this.state.imageName || ""}`.trim();
		if (!imageName) {
			this.setState({
				error: "Specify an image name before saving."
			});
			return;
		}
		if (!this.state.previewExportSafe) {
			this.setState({
				error: "This image preview is not yet canvas-safe, so it cannot be exported. Wait for the secure preview source to load."
			});
			return;
		}

		this.setState({
			saving: true,
			error: ""
		});

		try {
			const outputCanvas = this.createOutputCanvas();
			const blob = await this.canvasToBlob(outputCanvas);
			if (this.props.onCreate) {
				this.props.onCreate({
					blob,
					imageName: this.state.overwriteImage ? imageName : normalizePngFileName(imageName),
					autoLink: this.state.autoLinkImage,
					overwriteImage: this.state.overwriteImage,
					overwriteTargetPath: this.props.overwriteTargetPath || ""
				});
			}
		} catch (err) {
			const message = err && err.message ? err.message : `${err}`;
			this.setState({
				error: `Image save failed: ${message}`
			});
		} finally {
			this.setState({
				saving: false
			});
		}
	}

	render() {
		const {
			open,
			onClose,
			imageLabel,
			autoLinkLabel,
			overwriteTargetPath
		} = this.props;
		const {
			autoLinkImage,
			blurPx,
			cropViewportHeight,
			cropViewportWidth,
			cropPreset,
			cropZoom,
			error,
			freeCropHeightPct,
			freeCropWidthPct,
			imageName,
			isDraggingCrop,
			overwriteImage,
			padTargetHeight,
			padTargetWidth,
			padToTargetSize,
			showMarkupModal,
			markupBorderColor,
			markupBorderRadius,
			markupBorderWidth,
			markupComponentType,
			markupFillColor,
			markupFillEnabled,
			markupItems,
			markupMouseDown,
			markupMouseX,
			markupMouseY,
			markupPreviewScale,
			markupRotation,
			markupShape,
			markupShowItemIds,
			markupSourceUrl,
			markupSourceImageSize,
			paddingPx,
			previewImageLoading,
			previewImageUrl,
			previewExportSafe,
			resizeEnabled,
			resizeHeight,
			resizeLockAspect,
			resizeWidth,
			saving,
			sourceReady
		} = this.state;

		if (!open) {
			return null;
		}

		const compactLayout = typeof window !== "undefined" && window.innerWidth < 1200;
		const colorPopoverContainer = typeof document !== "undefined" ? document.body : undefined;
		const colorPopoverStyle = { zIndex: 210000 };
		const viewportHeight = typeof window !== "undefined" ? window.innerHeight || 900 : 900;
		const overlayStyle = {
			position: "fixed",
			inset: 0,
			backgroundColor: "rgba(15, 23, 42, 0.45)",
			zIndex: 200000,
			display: "flex",
			alignItems: "stretch",
			justifyContent: "stretch",
			padding: "12px",
			boxSizing: "border-box"
		};
		const dialogStyle = {
			flex: "1 1 auto",
			minWidth: 0,
			minHeight: 0,
			backgroundColor: "#ffffff",
			borderRadius: "24px",
			boxSizing: "border-box",
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
			maxHeight: "calc(100vh - 24px)",
			boxShadow: "0 20px 60px rgba(15, 23, 42, 0.28)"
		};
		const headerStyle = {
			padding: "22px 28px 10px 28px",
			borderBottom: "1px solid #e5e7eb",
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: "16px"
		};
		const bodyStyle = {
			flex: "1 1 auto",
			minHeight: 0,
			overflow: "auto",
			padding: "18px 28px 12px 28px",
			display: "flex",
			flexDirection: "column",
			gap: "16px"
		};
		const footerStyle = {
			borderTop: "1px solid #e5e7eb",
			padding: "14px 28px 18px 28px",
			display: "flex",
			justifyContent: "space-between",
			gap: "12px",
			flexWrap: "wrap",
			backgroundColor: "#ffffff"
		};
		const previewPaneHeight = compactLayout
			? Math.min(Math.max(viewportHeight - 560, 260), 520)
			: Math.min(Math.max(viewportHeight - 470, 340), 620);
		const markupEditorHeight = compactLayout
			? Math.min(Math.max(viewportHeight - 260, 420), 760)
			: Math.min(Math.max(viewportHeight - 220, 520), 900);
		const editorGridStyle = {
			display: "grid",
			gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 3fr) minmax(360px, 1fr)",
			gap: "24px",
			alignItems: "start",
			minWidth: 0
		};
		const toolRailStyle = {
			display: "flex",
			flexDirection: "column",
			gap: "16px",
			minWidth: 0,
			padding: compactLayout ? 0 : "18px",
			border: compactLayout ? "none" : "1px solid #e5e7eb",
			borderRadius: compactLayout ? 0 : "16px",
			background: compactLayout ? "transparent" : "#fbfcfe"
		};
		const toolSectionStyle = {
			display: "flex",
			flexDirection: "column",
			gap: "12px",
			minWidth: 0
		};
		const checkboxLabelStyle = {
			display: "flex",
			alignItems: "center",
			gap: "10px",
			margin: 0,
			color: "#374151",
			fontWeight: 500
		};
		const checkboxHelpStyle = {
			color: "#6b7280",
			cursor: "help",
			fontSize: "14px"
		};
		const cropMetrics = this.getCropMetrics();
		const outputDimensions = sourceReady ? this.getOutputContentDimensions({
			width: this.getCurrentCropSourceDimensions(cropMetrics).width,
			height: this.getCurrentCropSourceDimensions(cropMetrics).height
		}) : { width: 0, height: 0 };
		const outputFrameConfig = sourceReady ? this.getOutputFrameConfig(outputDimensions) : { width: 1, height: 1, offsetX: 0, offsetY: 0, mode: "uniform" };
		const outputFrameWidth = Math.max(outputFrameConfig.width || 1, 1);
		const outputFrameHeight = Math.max(outputFrameConfig.height || 1, 1);
		let previewFrameMetrics = null;
		if (cropMetrics && cropViewportWidth && cropViewportHeight && outputDimensions.width && outputDimensions.height) {
			const fitScale = Math.min(
				cropViewportWidth / outputFrameWidth,
				cropViewportHeight / outputFrameHeight
			);
			const displayScale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
			const outerDisplayWidth = Math.max(1, outputFrameWidth * displayScale);
			const outerDisplayHeight = Math.max(1, outputFrameHeight * displayScale);
			const contentDisplayWidth = Math.max(1, outputDimensions.width * displayScale);
			const contentDisplayHeight = Math.max(1, outputDimensions.height * displayScale);
			const contentLeft = Math.max((outputFrameConfig.offsetX || 0) * displayScale, 0);
			const contentTop = Math.max((outputFrameConfig.offsetY || 0) * displayScale, 0);
			const contentScaleX = contentDisplayWidth / cropMetrics.viewportWidth;
			const contentScaleY = contentDisplayHeight / cropMetrics.viewportHeight;

			previewFrameMetrics = {
				outerDisplayWidth,
				outerDisplayHeight,
				contentDisplayWidth,
				contentDisplayHeight,
				contentLeft,
				contentTop,
				imageLeft: cropMetrics.imageLeft * contentScaleX,
				imageTop: cropMetrics.imageTop * contentScaleY,
				imageWidth: cropMetrics.displayWidth * contentScaleX,
				imageHeight: cropMetrics.displayHeight * contentScaleY
			};
		}

		if (showMarkupModal && markupSourceUrl) {
			const selectedMarkupItem = this.getSelectedMarkupItem();
			const selectedMarkupVisible = selectedMarkupItem ? selectedMarkupItem.visible !== false : true;
			const markupDraftItem = this.getMarkupTempItem();
			const markupPreviewScaleRatio = markupPreviewScale / 100;
			const markupCanvasWidth = markupSourceImageSize ? markupSourceImageSize.width : 1;
			const markupCanvasHeight = markupSourceImageSize ? markupSourceImageSize.height : 1;
			const markupBorderWidthOptions = MARKUP_BORDER_WIDTH_OPTIONS.map((option) => (
				<option key={`markup-border-width-${option}`} value={option}>{option}</option>
			));
			const markupBorderRadiusOptions = MARKUP_BORDER_RADIUS_OPTIONS.map((option) => (
				<option key={`markup-border-radius-${option}`} value={option}>{option}</option>
			));

			return (
				<div style={overlayStyle} onMouseDown={onClose}>
					<div style={dialogStyle} onMouseDown={(event) => event.stopPropagation()}>
						<div style={headerStyle}>
							<div style={{ minWidth: 0, flex: 1 }}>
								<h3 style={{ margin: 0, textAlign: "center" }}>Edit Image</h3>
								<div style={{ marginTop: "10px", textAlign: "center", color: "#666666" }}>Markup</div>
							</div>
							<Button bsStyle="link" onClick={onClose} style={{ color: "#111827", padding: 0, fontSize: "28px", lineHeight: 1 }}>
								<FontAwesomeIcon icon="times" />
							</Button>
						</div>
						<div style={{ ...bodyStyle, padding: "14px 18px 14px 18px" }}>
							<ButtonToolbar style={{ flex: "0 0 auto", backgroundColor: "#dddddd", padding: "10px 12px", borderRadius: "12px", width: "100%" }}>
								<div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
									<ButtonGroup>
										<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-evenly", flexWrap: "wrap", gap: "10px" }}>
											<Button style={{ marginRight: "18px", display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }} bsSize="large" bsStyle="primary" onClick={this.handleApplyInlineMarkup} title="Apply markup to the image">
												<FontAwesomeIcon icon="save" />
												<div style={{ fontSize: "12px", paddingLeft: "5px" }}>Apply</div>
											</Button>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Select Component</div>
												<select onChange={this.handleMarkupComponentTypeChange} value={markupComponentType}>
													<option value="highlight">Highlight</option>
													<option value="hotspot">Hotspot</option>
												</select>
											</div>
											<div style={{ marginLeft: "8px", marginRight: "8px" }}>|</div>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Shape</div>
												<select onChange={this.handleMarkupShapeChange} value={markupShape}>
													<option value="rectangle">Rectangle</option>
													<option value="arrow">Arrow</option>
												</select>
											</div>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Border Color</div>
												<ColorPicker
													id="popover-inline-markup-border-color"
													title="Markup Border Color"
													onChangeColor={this.handleMarkupBorderColorChange}
													onClearColor={() => {}}
													presetColors={env.defaultColors}
													color={markupBorderColor}
													placement="bottom"
													hideClear={true}
													disableAlpha={false}
													container={colorPopoverContainer}
													popoverStyle={colorPopoverStyle}
												/>
											</div>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Border Width</div>
												<select onChange={this.handleMarkupBorderWidthChange} value={markupBorderWidth}>
													{markupBorderWidthOptions}
												</select>
											</div>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Border Radius</div>
												<select onChange={this.handleMarkupBorderRadiusChange} value={markupShape === "arrow" ? 0 : markupBorderRadius} disabled={markupShape === "arrow"}>
													{markupBorderRadiusOptions}
												</select>
											</div>
											<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: "18px 6px 0 0", fontWeight: 500 }}>
												<input type="checkbox" checked={markupFillEnabled} onChange={this.handleMarkupFillEnabledChange} />
												<span>Fill</span>
											</label>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center", marginRight: 10 }}>
												<div style={{ fontSize: "x-small" }}>Border Fill Color</div>
												<ColorPicker
													id="popover-inline-markup-fill-color"
													title="Markup Fill Color"
													onChangeColor={this.handleMarkupFillColorChange}
													onClearColor={() => {}}
													presetColors={env.defaultColors}
													color={markupFillEnabled ? markupFillColor : "transparent"}
													placement="bottom"
													hideClear={true}
													disableAlpha={false}
													container={colorPopoverContainer}
													popoverStyle={colorPopoverStyle}
												/>
											</div>
										</div>
									</ButtonGroup>
									<ButtonGroup>
										<div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-evenly", paddingRight: 12, gap: "12px", flexWrap: "wrap" }}>
											<Button style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center" }} bsStyle="primary" onClick={this.handleMarkupToggleLabels} title={markupShowItemIds ? "Hide item labels on canvas" : "Show item labels on canvas"}>
												<FontAwesomeIcon style={{ fontSize: "12px" }} icon={markupShowItemIds ? "align-slash" : "align-justify"} />
												<div style={{ fontSize: "12px", paddingLeft: "5px" }}>{markupShowItemIds ? "Hide Labels" : "Show Labels"}</div>
											</Button>
											<div style={{ height: 50, display: "flex", flexDirection: "column", alignItems: "center" }}>
												<div style={{ fontSize: "x-small" }}>Zoom</div>
												<select style={{ borderRadius: "5px", border: "1px solid #cccccc", marginBottom: 0 }} value={markupPreviewScale} onChange={this.handleMarkupPreviewScaleChange}>
													{MARKUP_ZOOM_OPTIONS.map((option) => (
														<option key={`markup-zoom-${option}`} value={option}>{option}%</option>
													))}
												</select>
											</div>
											<div style={{ fontFamily: "Courier", fontSize: "13px", color: "#0f172a", background: "#dbeafe", borderRadius: "8px", padding: "8px 10px", minWidth: "110px", textAlign: "center" }}>
												{markupMouseX}, {markupMouseY}
											</div>
										</div>
									</ButtonGroup>
								</div>
							</ButtonToolbar>
							<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 4fr) minmax(280px, 1fr)", gap: "22px", minWidth: 0 }}>
								<div style={{ border: "1px solid #dbe2ea", borderRadius: "16px", background: "#f8fafc", minHeight: `${markupEditorHeight}px`, height: `${markupEditorHeight}px`, overflow: "auto", padding: "20px" }}>
									<div style={{ width: `${markupCanvasWidth * markupPreviewScaleRatio}px`, height: `${markupCanvasHeight * markupPreviewScaleRatio}px`, minWidth: `${markupCanvasWidth * markupPreviewScaleRatio}px`, minHeight: `${markupCanvasHeight * markupPreviewScaleRatio}px` }}>
										<svg
											ref={this.markupSvgRef}
											viewBox={`0 0 ${markupCanvasWidth} ${markupCanvasHeight}`}
											width={markupCanvasWidth * markupPreviewScaleRatio}
											height={markupCanvasHeight * markupPreviewScaleRatio}
											style={{
												display: "block",
												background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
												backgroundSize: "24px 24px",
												backgroundPosition: "0 0, 12px 12px",
												boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
												cursor: markupMouseDown ? "crosshair" : "crosshair"
											}}
											onMouseDown={this.handleMarkupMouseDown}
											onMouseMove={this.handleMarkupMouseMove}
											onMouseUp={this.handleMarkupMouseUp}
											onMouseLeave={this.handleMarkupMouseUp}
											onClick={this.handleMarkupCanvasClick}
										>
											<image href={markupSourceUrl} x="0" y="0" width={markupCanvasWidth} height={markupCanvasHeight} />
											{markupItems.map((item) => this.renderMarkupSvgItem(item))}
											{markupDraftItem && this.renderMarkupSvgItem(markupDraftItem, { isDraft: true })}
										</svg>
									</div>
								</div>
								<div style={toolRailStyle}>
									<div style={toolSectionStyle}>
										<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Markup</div>
										<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
											Drag on the image to draw a {markupShape === "arrow" ? "filled arrow" : `${markupComponentType} rectangle`}.
											Use the zoom control for precision, then click a markup to select or delete it.
										</div>
									</div>
									<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
										<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Selected markup</div>
										{selectedMarkupItem ? (
											<>
												<div style={{ color: "#111827", fontWeight: 600 }}>{selectedMarkupItem.id}</div>
												<div style={{ color: "#6b7280", fontSize: "12px" }}>{selectedMarkupItem.shape === "arrow" ? "Arrow" : `${selectedMarkupItem.type || "highlight"} rectangle`}</div>
												<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
													x: {selectedMarkupItem.coords.x}, y: {selectedMarkupItem.coords.y}, width: {selectedMarkupItem.coords.width}, height: {selectedMarkupItem.coords.height}
												</div>
												<div style={{ color: "#4b5563", fontSize: "13px", lineHeight: 1.5 }}>
													rotation: {normalizeMarkupRotation(selectedMarkupItem.rotation || 0)}°
												</div>
												<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
													<Button bsSize="small" title="Clone selected markup" onClick={this.handleCloneSelectedMarkup}>
														<FontAwesomeIcon icon="clone" />
													</Button>
													<Button bsSize="small" title={selectedMarkupVisible ? "Hide selected markup" : "Show selected markup"} onClick={this.handleToggleSelectedMarkupVisibility}>
														<FontAwesomeIcon icon={selectedMarkupVisible ? "eye" : "eye-slash"} />
													</Button>
													<Button
														bsSize="small"
														title="Move left"
														onClick={() => this.handleNudgeSelectedMarkup(-1, 0)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(-step, 0))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="arrow-left" />
													</Button>
													<Button
														bsSize="small"
														title="Move right"
														onClick={() => this.handleNudgeSelectedMarkup(1, 0)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(step, 0))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="arrow-right" />
													</Button>
													<Button
														bsSize="small"
														title="Move up"
														onClick={() => this.handleNudgeSelectedMarkup(0, -1)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(0, -step))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="arrow-up" />
													</Button>
													<Button
														bsSize="small"
														title="Move down"
														onClick={() => this.handleNudgeSelectedMarkup(0, 1)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleNudgeSelectedMarkup(0, step))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="arrow-down" />
													</Button>
													<Button
														bsSize="small"
														title="Rotate left"
														onClick={() => this.handleRotateSelectedMarkup(-1)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(-step))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="undo" />
													</Button>
													<Button
														bsSize="small"
														title="Rotate right"
														onClick={() => this.handleRotateSelectedMarkup(1)}
														onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(step))}
														onMouseLeave={this.stopMarkupPressAndHold}
													>
														<FontAwesomeIcon icon="redo" />
													</Button>
													<Button bsStyle="danger" bsSize="small" title="Delete selected markup" onClick={this.handleDeleteSelectedMarkupItem}>
														<FontAwesomeIcon icon="trash" />
													</Button>
												</div>
												<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
													<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Rotation</div>
													<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
														<Button
															bsSize="small"
															onClick={() => this.handleRotateSelectedMarkup(-1)}
															onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(-step))}
															onMouseLeave={this.stopMarkupPressAndHold}
															title="Rotate left"
														>
															-1°
														</Button>
														<input
															className="form-control"
															type="number"
															value={markupRotation}
															onChange={this.handleMarkupRotationChange}
															style={{ width: "88px" }}
														/>
														<Button
															bsSize="small"
															onClick={() => this.handleRotateSelectedMarkup(1)}
															onMouseDown={() => this.startMarkupPressAndHold((step) => this.handleRotateSelectedMarkup(step))}
															onMouseLeave={this.stopMarkupPressAndHold}
															title="Rotate right"
														>
															+1°
														</Button>
													</div>
												</div>
											</>
										) : (
											<div style={{ color: "#6b7280", fontSize: "13px" }}>Click a markup on the image to inspect or delete it.</div>
										)}
									</div>
									{markupItems.length > 0 && (
										<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
											<div style={{ color: "#6b7280", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>Markup list</div>
											<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
												{markupItems.map((item) => (
													<button
														key={item.guid}
														type="button"
														onClick={() => this.handleSelectMarkupItem(item.guid)}
														style={{
															textAlign: "left",
															border: item.guid === this.state.selectedMarkupGuid ? "1px solid #2563eb" : "1px solid #d1d5db",
															background: item.guid === this.state.selectedMarkupGuid ? "#eff6ff" : "#ffffff",
															borderRadius: "10px",
															padding: "10px 12px",
															color: "#111827",
															opacity: item.visible === false ? 0.55 : 1
														}}
													>
														<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
															<div style={{ fontWeight: 600 }}>{item.id}</div>
															<Button
																bsSize="small"
																onClick={(event) => this.handleToggleMarkupVisibility(item.guid, event)}
																title={item.visible === false ? "Show markup" : "Hide markup"}
																style={{ padding: "2px 6px" }}
															>
																<FontAwesomeIcon icon={item.visible === false ? "eye-slash" : "eye"} />
															</Button>
														</div>
														<div style={{ fontSize: "12px", color: "#6b7280" }}>
															{item.shape === "arrow" ? "Arrow" : `${item.type || "highlight"} rectangle`} • {item.coords.width} x {item.coords.height}
															{item.visible === false ? " • hidden" : ""}
														</div>
													</button>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
						<div style={footerStyle}>
							<Button onClick={this.handleCloseMarkupModal}>
								<FontAwesomeIcon icon="arrow-left" /> Back to Edit
							</Button>
							<div style={{ color: "#6b7280", fontSize: "13px", alignSelf: "center" }}>Markup is flattened into the image when you apply it.</div>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div style={overlayStyle} onMouseDown={onClose}>
				<div style={dialogStyle} onMouseDown={(event) => event.stopPropagation()}>
					<div style={headerStyle}>
						<div style={{ minWidth: 0, flex: 1 }}>
							<h3 style={{ margin: 0, textAlign: "center" }}>Edit Image</h3>
							{imageLabel && <div style={{ marginTop: "10px", textAlign: "center", color: "#666666" }}>{imageLabel}</div>}
						</div>
						<Button bsStyle="link" onClick={onClose} style={{ color: "#111827", padding: 0, fontSize: "28px", lineHeight: 1 }}>
							<FontAwesomeIcon icon="times" />
						</Button>
					</div>
					<div style={bodyStyle}>
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<label style={{ color: "gray" }}>Image name</label>
							<input className="form-control" type="text" value={imageName} onChange={this.handleImageNameChange} placeholder="image-edited.png" disabled={overwriteImage} />
						</div>
						<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px 24px" }}>
							<label style={checkboxLabelStyle}>
								<input type="checkbox" checked={autoLinkImage} onChange={this.handleToggleAutoLinkImage} />
								<span>{autoLinkLabel || "Auto-link Image"}</span>
								<span title="After saving, update the item's image property to the saved file." style={checkboxHelpStyle}>
									<FontAwesomeIcon icon="info-circle" />
								</span>
							</label>
							{!!overwriteTargetPath && (
								<label style={checkboxLabelStyle}>
									<input type="checkbox" checked={overwriteImage} onChange={this.handleToggleOverwriteImage} />
									<span>Overwrite Image</span>
									<span title="Save back to the exact Drive image you are editing instead of creating a new file." style={checkboxHelpStyle}>
										<FontAwesomeIcon icon="info-circle" />
									</span>
								</label>
							)}
						</div>
						{!previewImageLoading && previewImageUrl && !previewExportSafe && (
							<div style={{ color: "#92400e", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: "10px", padding: "10px 12px" }}>
								This image is available for preview, but the browser has not loaded a canvas-safe source yet, so save is temporarily disabled.
							</div>
						)}
						<div style={editorGridStyle}>
							<div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
									<div style={{ color: "gray" }}>Image preview</div>
									<Button bsSize="small" onClick={this.handleOpenMarkupModal} disabled={!sourceReady || previewImageLoading || !previewExportSafe}>
										<FontAwesomeIcon icon="pen" /> Markup
									</Button>
								</div>
								<div
									ref={this.cropViewportRef}
									style={{
										position: "relative",
										border: "1px solid #dddddd",
										borderRadius: "16px",
										height: `${previewPaneHeight}px`,
										maxHeight: `${previewPaneHeight}px`,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										background: "#f8fafc",
										overflow: "hidden",
										padding: compactLayout ? "18px" : "28px"
									}}
								>
									{previewImageUrl && (
										<img
											ref={this.imageRef}
											alt="Editable preview"
											src={previewImageUrl}
											onLoad={this.handleImageLoaded}
											onError={this.handleImageError}
											style={{ display: "none" }}
										/>
									)}
									{sourceReady && previewFrameMetrics ? (
										<div
											onMouseDown={this.handleCropDragStart}
											style={{
												width: `${previewFrameMetrics.outerDisplayWidth}px`,
												height: `${previewFrameMetrics.outerDisplayHeight}px`,
												maxWidth: "100%",
												maxHeight: "100%",
												position: "relative",
												overflow: "hidden",
												borderRadius: "12px",
												background: "linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7), linear-gradient(45deg, #eef2f7 25%, transparent 25%, transparent 75%, #eef2f7 75%, #eef2f7)",
												backgroundSize: "24px 24px",
												backgroundPosition: "0 0, 12px 12px",
												boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.08)",
												cursor: isDraggingCrop ? "grabbing" : "grab"
											}}
										>
											<div
												style={{
													position: "absolute",
													left: `${previewFrameMetrics.contentLeft}px`,
													top: `${previewFrameMetrics.contentTop}px`,
													width: `${previewFrameMetrics.contentDisplayWidth}px`,
													height: `${previewFrameMetrics.contentDisplayHeight}px`,
													overflow: "hidden",
													borderRadius: "10px",
													boxShadow: "inset 0 0 0 1px rgba(15, 23, 42, 0.06)"
												}}
											>
												<img
													alt="Preview crop"
													src={previewImageUrl}
													draggable={false}
													onDragStart={(event) => event.preventDefault()}
													style={{
														position: "absolute",
														left: `${previewFrameMetrics.imageLeft}px`,
														top: `${previewFrameMetrics.imageTop}px`,
														width: `${previewFrameMetrics.imageWidth}px`,
														height: `${previewFrameMetrics.imageHeight}px`,
														maxWidth: "none",
														userSelect: "none",
														pointerEvents: "none",
														filter: blurPx > 0 ? `blur(${blurPx}px)` : "none"
													}}
												/>
											</div>
										</div>
									) : (
										<div style={{ padding: "20px", color: "#777777", textAlign: "center" }}>
											{previewImageLoading || sourceReady ? "Preparing preview..." : "Select a valid image to edit."}
										</div>
									)}
									{previewImageLoading && (
										<div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255, 255, 255, 0.55)", color: "#374151", fontWeight: 600 }}>
											<FontAwesomeIcon icon="spinner" spin style={{ marginRight: "8px" }} /> Loading image...
										</div>
									)}
								</div>
								<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.4 }}>
									Drag inside the preview to reframe the crop. Blur, resize, and padding are previewed live.
								</div>
							</div>
							<div style={toolRailStyle}>
								<div style={toolSectionStyle}>
									<div style={{ color: "#666666", fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
										Crop preset
									</div>
									<div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
										{CROP_PRESET_OPTIONS.map((option) => (
											<Button
												key={option.value}
												bsSize="small"
												bsStyle={cropPreset === option.value ? "primary" : "default"}
												active={cropPreset === option.value}
												onClick={() => this.handleChangeCropPreset(option.value)}
												disabled={!sourceReady}
											>
												{option.label}
											</Button>
										))}
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Zoom
										</div>
										<input
											type="range"
											min={DEFAULT_CROP_ZOOM}
											max={MAX_CROP_ZOOM}
											step={0.05}
											value={cropZoom}
											onInput={this.handleCropZoomChange}
											onChange={this.handleCropZoomChange}
											disabled={!sourceReady}
											style={{ flex: 1 }}
										/>
										<div style={{ minWidth: "44px", textAlign: "right", color: "#666666" }}>
											{cropZoom.toFixed(2)}x
										</div>
									</div>
									{cropPreset === FREE_CROP_PRESET && (
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Width
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropWidthPct}
													onInput={this.handleFreeCropWidthChange}
													onChange={this.handleFreeCropWidthChange}
													disabled={!sourceReady}
													style={{ flex: 1 }}
												/>
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
												<div style={{ minWidth: "68px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
													Height
												</div>
												<input
													type="range"
													min={MIN_FREE_CROP_SCALE}
													max={1}
													step={0.01}
													value={freeCropHeightPct}
													onInput={this.handleFreeCropHeightChange}
													onChange={this.handleFreeCropHeightChange}
													disabled={!sourceReady}
													style={{ flex: 1 }}
												/>
											</div>
										</div>
									)}
									<div style={{ display: "flex", justifyContent: "flex-end" }}>
										<Button bsSize="small" onClick={this.handleResetCrop} disabled={!sourceReady}>
											Reset Crop
										</Button>
									</div>
								</div>
								<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
									<div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, fontWeight: 600 }}>
											<input type="checkbox" checked={resizeEnabled} onChange={this.handleToggleResizeEnabled} />
											<span>Resize output</span>
										</label>
										<label style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, color: "#666666" }}>
											<input type="checkbox" checked={resizeLockAspect} onChange={this.handleToggleResizeLock} disabled={!resizeEnabled} />
											<span>Lock ratio</span>
										</label>
									</div>
									<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Width</label>
											<input className="form-control" type="number" min="1" value={resizeWidth} disabled={!resizeEnabled} onChange={this.handleResizeWidthChange} />
										</div>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Height</label>
											<input className="form-control" type="number" min="1" value={resizeHeight} disabled={!resizeEnabled} onChange={this.handleResizeHeightChange} />
										</div>
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "88px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Blur
										</div>
										<input
											type="range"
											min={0}
											max={MAX_BLUR_PX}
											step={0.5}
											value={blurPx}
											onInput={this.handleBlurChange}
											onChange={this.handleBlurChange}
											disabled={!sourceReady}
											style={{ flex: 1 }}
										/>
										<div style={{ minWidth: "48px", textAlign: "right", color: "#666666" }}>
											{blurPx.toFixed(1)}px
										</div>
									</div>
									<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
										<div style={{ minWidth: "88px", color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
											Padding
										</div>
										<input
											type="range"
											min={0}
											max={Math.max(MAX_PADDING_SLIDER_PX, paddingPx)}
											step={1}
											value={paddingPx}
											onInput={this.handlePaddingChange}
											onChange={this.handlePaddingChange}
											disabled={!sourceReady || padToTargetSize}
											style={{ flex: 1 }}
										/>
									</div>
									<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) 124px", gap: "10px", alignItems: "end" }}>
										<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
											Use the slider for quick adjustment, or type a larger padding value directly.
										</div>
										<div>
											<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Padding px</label>
											<input
												className="form-control"
												type="number"
												min="0"
												max={MAX_PADDING_INPUT_PX}
												value={paddingPx}
												disabled={!sourceReady || padToTargetSize}
												onChange={this.handlePaddingChange}
											/>
										</div>
									</div>
									<div style={{ ...toolSectionStyle, paddingTop: "14px", borderTop: "1px solid #e5e7eb" }}>
										<label style={{ display: "flex", alignItems: "center", gap: "10px", margin: 0, fontWeight: 600 }}>
											<input type="checkbox" checked={padToTargetSize} onChange={this.handleTogglePadToTargetSize} />
											<span>Pad to final size</span>
										</label>
										<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
											Enter the exact final output width and height. The current image is centered inside that transparent frame.
										</div>
										<div style={{ display: "grid", gridTemplateColumns: compactLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
											<div>
												<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Final width</label>
												<input className="form-control" type="number" min="1" max={MAX_FINAL_OUTPUT_DIMENSION_PX} value={padTargetWidth} disabled={!padToTargetSize} onChange={this.handlePadTargetWidthChange} />
											</div>
											<div>
												<label style={{ color: "#666666", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Final height</label>
												<input className="form-control" type="number" min="1" max={MAX_FINAL_OUTPUT_DIMENSION_PX} value={padTargetHeight} disabled={!padToTargetSize} onChange={this.handlePadTargetHeightChange} />
											</div>
										</div>
									</div>
									<div style={{ color: "#666666", fontSize: "12px", lineHeight: 1.5 }}>
										Output size: <b>{outputFrameWidth || 0} x {outputFrameHeight || 0}</b>
										{padToTargetSize
											? " with the current image centered inside the final frame"
											: (paddingPx > 0 ? ` plus ${paddingPx}px transparent padding on each side` : "")}
									</div>
								</div>
							</div>
						</div>
						{error && (
							<div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "12px 14px" }}>
								{error}
							</div>
						)}
					</div>
					<div style={footerStyle}>
						<Button onClick={onClose}>
							<FontAwesomeIcon icon="ban" /> Cancel
						</Button>
						<Button bsStyle="primary" onClick={this.handleSaveImage} disabled={!sourceReady || saving || previewImageLoading || !previewExportSafe}>
							<FontAwesomeIcon icon={saving ? "spinner" : "save"} spin={saving} /> {saving ? "Saving..." : "Save Image"}
						</Button>
					</div>
					<canvas ref={this.canvasRef} style={{ display: "none" }} />
				</div>
			</div>
		);
	}
}
